import ReportFigure from '@components/Reporting/ReportFigure';
import Header from '@components/Shared/Layout/Header';
import { Loading, LoadFailed } from '@components/Shared/LoadState';
import {
  Alert, Box, Card, CardContent, Chip, MenuItem, Pagination, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Tooltip, Typography
} from '@mui/material';
import { useGetLicensesQuery } from '@store/api/licenseEndpoints';
import { useGetOfflineGrantsQuery, type OfflineGrantQuery } from '@store/api/offlineUsageEndpoints';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const FILTERS: { value: NonNullable<OfflineGrantQuery['reported']>, label: string }[] = [
  { value: 'reported', label: 'With reported use' },
  { value: 'all', label: 'All grants' },
  { value: 'never', label: 'Never reported' },
];

/**
 * Offline usage: what devices used while they could not reach this server.
 *
 * A device that is out of contact keeps working after its seat window ends, on the grant it was
 * given with its last seat, and reports the time it used when it is next in contact. A device
 * working that way holds no seat, so it never appears on the Seat sessions page; this is where its
 * use shows up, including the part that ran while every seat was taken.
 */
const OfflineUsagePage = () => {
  // The licence is in the address, so the Seat usage page can link to one licence's offline use.
  const [searchParams, setSearchParams] = useSearchParams();
  const licenseId = searchParams.get('licenseId') ?? '';
  const [reported, setReported] = useState<NonNullable<OfflineGrantQuery['reported']>>('reported');
  const [page, setPage] = useState(1);

  const { data: licenses = [] } = useGetLicensesQuery();
  // The page for the filters on screen only (`currentData`), so that neither a filter still loading
  // nor one that failed shows the previous filter's grants and figures under it.
  const { currentData: data, isError, refetch } = useGetOfflineGrantsQuery({
    licenseId: licenseId || undefined, reported, page, limit: 50
  });

  const licenseName = (id: string) => {
    const license = licenses.find(item => item.licenseId === id);

    return license?.label || license?.id || id.slice(0, 8);
  };

  const totals = data?.totals;

  return (
    <Box sx={{ width: '100%' }}>
      <Header
        title='Offline usage'
        description='Time devices worked while out of contact, after their seat window ended.'
      />

      <Alert severity='info' sx={{ mb: 2 }}>
        Every seat a device takes or keeps comes with an offline allowance: for a set time after
        that (one week unless the license says otherwise), it may keep working for a limited time
        if it cannot reach this server. It records the time it uses and reports it here when it
        is next in contact. A device working this way
        holds no seat, so it does not appear under Seat sessions. <strong>Never reported</strong> is
        normal: most devices never run past their seat window, and app builds older than the
        October 2026 release take the allowance but cannot report.
      </Alert>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          {/* A gap rather than margins, so a wrapped second row starts where the first did. */}
          <Stack direction='row' spacing={2} useFlexGap sx={{ mb: 3, flexWrap: 'wrap', rowGap: 2 }}>
            <TextField
              select size='small' label='License' value={licenseId} sx={{ minWidth: 200 }}
              // "All licenses" is a choice like any other, so it shows as the value, not as an empty field.
              slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
              onChange={event => {
                setSearchParams(event.target.value ? { licenseId: event.target.value } : {}, { replace: true });
                setPage(1);
              }}
            >
              <MenuItem value=''>All licenses</MenuItem>
              {licenses.map(license => (
                <MenuItem key={license.licenseId} value={license.licenseId}>{licenseName(license.licenseId)}</MenuItem>
              ))}
            </TextField>

            <TextField
              select size='small' label='Show' value={reported} sx={{ minWidth: 190 }}
              onChange={event => { setReported(event.target.value as typeof reported); setPage(1); }}
            >
              {FILTERS.map(item => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
            </TextField>
          </Stack>

          {/* The time is the server's: each ticket at the seconds it claims, as Activity and Seat
              usage count it, so a ticket is not taken for fifteen minutes here. */}
          <Stack direction='row' spacing={4} useFlexGap sx={{ flexWrap: 'wrap', rowGap: 2 }}>
            <ReportFigure
              label='Offline use reported'
              value={totals?.reportedTickets ? totals.reportedSeconds.formatDuration() : '-'}
              hint={totals ? totals.reportedTickets.formatCount('ticket', 'tickets') : undefined}
            />
            {/* Time, like the column below and the Seat usage page, with the tickets under it. */}
            <ReportFigure
              label='Beyond the limit'
              value={totals?.beyondCapacityTickets ? totals.beyondCapacitySeconds.formatDuration() : '-'}
              hint={totals
                ? `${totals.beyondCapacityTickets.formatCount('ticket', 'tickets')} while every seat was taken`
                : undefined}
            />
            <ReportFigure
              label='Grants'
              value={totals ? String(totals.grants) : '-'}
              hint={totals ? `${totals.neverReported} never reported` : undefined}
            />
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {/* "None" only once the answer says so: not while it loads, and not when it failed. */}
          {isError ? (
            <LoadFailed text='The offline usage could not be loaded.' onRetry={() => { void refetch(); }} />
          ) : !data ? (
            <Loading text='Loading the offline usage…' />
          ) : !data.items.length ? (
            <Typography variant='body2' color='text.secondary'>
              {reported === 'reported' ? 'No offline use has been reported.' : 'No grants to show.'}
            </Typography>
          ) : (
            <>
              {/* Scrolls sideways on a narrow screen rather than cutting off the columns on the right. */}
              <Box sx={{ overflowX: 'auto' }}>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>License</TableCell>
                      <TableCell>Application</TableCell>
                      <TableCell>Last seat</TableCell>
                      <TableCell>Allowance</TableCell>
                      <TableCell align='right'>Reported</TableCell>
                      <TableCell align='right'>Beyond the limit</TableCell>
                      <TableCell>Last report</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.items.map(grant => (
                      <TableRow key={grant.grantId}>
                        <TableCell>{licenseName(grant.licenseId)}</TableCell>
                        <TableCell>{grant.app === 'supar' ? 'SuPAR' : 'Composer'}</TableCell>
                        <TableCell>
                          {new Date(grant.refreshedAt).toFormattedDateTimeString()}
                          {grant.holdsSeat && <Chip size='small' variant='outlined' color='success' label='holds a seat' sx={{ ml: 1 }} />}
                        </TableCell>
                        <TableCell>
                          {grant.budgetSeconds.formatDuration()} until{' '}
                          {new Date(grant.expiresAt).toFormattedDateTimeString()}
                        </TableCell>
                        <TableCell align='right'>
                          {grant.neverReported ? (
                            <Typography variant='caption' color='text.secondary'>never reported</Typography>
                          ) : grant.reportedSeconds.formatDuration()}
                        </TableCell>
                        <TableCell align='right'>
                          {grant.beyondCapacityTickets
                            ? <Chip size='small' variant='outlined' color='warning' label={grant.beyondCapacitySeconds.formatDuration()} />
                            : '-'}
                        </TableCell>
                        <TableCell>
                          {grant.lastReportedAt ? new Date(grant.lastReportedAt).toFormattedDateTimeString() : '-'}
                          {grant.anomalyCount > 0 && (
                            <Tooltip title={`${grant.lastAnomaly?.kind ?? ''} ${grant.lastAnomaly?.detail ?? ''}. Recorded for diagnosis; the report was accepted.`}>
                              <Chip size='small' variant='outlined' label={grant.anomalyCount.formatCount('anomaly', 'anomalies')} sx={{ ml: 1 }} />
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>

              <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mt: 2 }}>
                <Typography variant='caption' color='text.secondary'>{data.total.formatCount('grant', 'grants')}</Typography>
                <Pagination
                  size='small'
                  count={Math.max(1, Math.ceil(data.total / data.limit))}
                  page={data.page}
                  onChange={(_event, value) => setPage(value)}
                />
              </Stack>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default OfflineUsagePage;
